import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeySquare, CheckCircle2, XCircle, ExternalLink, Users } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  PageHeader,
  Button,
  Card,
  Input,
  LoadingState,
  ErrorState,
  Switch,
  Select,
} from '@/components/ui'

interface AirkeyConfigStatus {
  configured: boolean
  baseUrl: string
}

interface TestResult {
  ok: boolean
  details?: string
  lockingSystemCount?: number
}

interface AirkeyOnboardingConfig {
  enabled: boolean
  defaultLockId: number | null
  defaultAreaId: number | null
  defaultLockingSystemId: number | null
}

interface AirkeyLock {
  id: number
  name: string
}

interface AirkeyArea {
  id: number
  name: string
}

interface AirkeyLockingSystem {
  id: number
  name: string
}

interface AirkeyListResponse<T> {
  data: T[]
  recordCount: number
}

function extractErrorMessage(err: unknown): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    String(err)
  )
}

export function AirkeySettingsPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [testResult, setTestResult] = useState<TestResult | null>(null)

  // Onboarding config local state
  const [onboardingEnabled, setOnboardingEnabled] = useState(false)
  const [defaultLockId, setDefaultLockId] = useState<string>('')
  const [defaultAreaId, setDefaultAreaId] = useState<string>('')
  const [defaultLockingSystemId, setDefaultLockingSystemId] = useState<string>('')

  const configQuery = useQuery<AirkeyConfigStatus>({
    queryKey: ['airkey', 'config'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyConfigStatus>('/airkey/config')
      return data
    },
    retry: false,
  })

  const onboardingConfigQuery = useQuery<AirkeyOnboardingConfig>({
    queryKey: ['airkey', 'onboarding-config'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyOnboardingConfig>('/airkey/onboarding-config')
      return data
    },
    enabled: configQuery.data?.configured === true,
    retry: false,
  })

  const locksQuery = useQuery<AirkeyListResponse<AirkeyLock>>({
    queryKey: ['airkey', 'locks'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyLock>>('/airkey/locks', {
        params: { limit: 200 },
      })
      return data
    },
    enabled: configQuery.data?.configured === true,
    retry: false,
  })

  const areasQuery = useQuery<AirkeyListResponse<AirkeyArea>>({
    queryKey: ['airkey', 'areas'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyArea>>('/airkey/areas', {
        params: { limit: 200 },
      })
      return data
    },
    enabled: configQuery.data?.configured === true,
    retry: false,
  })

  const lockingSystemsQuery = useQuery<AirkeyListResponse<AirkeyLockingSystem>>({
    queryKey: ['airkey', 'lockingsystems'],
    queryFn: async () => {
      const { data } = await apiClient.get<AirkeyListResponse<AirkeyLockingSystem>>(
        '/airkey/lockingsystems',
        { params: { limit: 200 } },
      )
      return data
    },
    enabled: configQuery.data?.configured === true,
    retry: false,
  })

  useEffect(() => {
    if (configQuery.data) {
      setBaseUrl(configQuery.data.baseUrl ?? 'https://integration.api.airkey.evva.com')
    }
  }, [configQuery.data])

  useEffect(() => {
    if (onboardingConfigQuery.data) {
      const cfg = onboardingConfigQuery.data
      setOnboardingEnabled(cfg.enabled)
      setDefaultLockId(cfg.defaultLockId !== null ? String(cfg.defaultLockId) : '')
      setDefaultAreaId(cfg.defaultAreaId !== null ? String(cfg.defaultAreaId) : '')
      setDefaultLockingSystemId(
        cfg.defaultLockingSystemId !== null ? String(cfg.defaultLockingSystemId) : '',
      )
    }
  }, [onboardingConfigQuery.data])

  const configured = configQuery.data?.configured ?? false

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!apiKey && !configured) {
        throw new Error('Bitte einen API-Key eingeben.')
      }
      const payload: Record<string, string> = {
        baseUrl: baseUrl || 'https://integration.api.airkey.evva.com',
      }
      if (apiKey) payload['apiKey'] = apiKey
      await apiClient.put('/airkey/config', payload)
    },
    onSuccess: () => {
      setTestResult(null)
      setApiKey('')
      qc.invalidateQueries({ queryKey: ['airkey', 'config'] })
    },
  })

  const saveOnboardingMut = useMutation({
    mutationFn: async () => {
      const payload: AirkeyOnboardingConfig = {
        enabled: onboardingEnabled,
        defaultLockId: defaultLockId ? parseInt(defaultLockId, 10) : null,
        defaultAreaId: defaultAreaId ? parseInt(defaultAreaId, 10) : null,
        defaultLockingSystemId: defaultLockingSystemId
          ? parseInt(defaultLockingSystemId, 10)
          : null,
      }
      await apiClient.put('/airkey/onboarding-config', payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['airkey', 'onboarding-config'] })
    },
  })

  const testMut = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.get<{ data: unknown[]; recordCount: number }>(
        '/airkey/lockingsystems',
        { params: { limit: 1 } },
      )
      return { ok: true, lockingSystemCount: data.recordCount } satisfies TestResult
    },
    onSuccess: (result) => setTestResult(result),
    onError: (err) => setTestResult({ ok: false, details: extractErrorMessage(err) }),
  })

  if (configQuery.isLoading) {
    return <LoadingState label="Konfiguration wird geladen…" />
  }

  const lockOptions = [
    { value: '', label: '— Kein Standard-Schloss —' },
    ...(locksQuery.data?.data ?? []).map((l) => ({ value: String(l.id), label: l.name })),
  ]
  const areaOptions = [
    { value: '', label: '— Kein Standard-Bereich —' },
    ...(areasQuery.data?.data ?? []).map((a) => ({ value: String(a.id), label: a.name })),
  ]
  const lockingSystemOptions = [
    { value: '', label: '— Keine Standard-Schließanlage —' },
    ...(lockingSystemsQuery.data?.data ?? []).map((ls) => ({
      value: String(ls.id),
      label: ls.name,
    })),
  ]

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <PageHeader
        title="Airkey-Einstellungen"
        breadcrumbs={[
          { label: 'Einstellungen', to: '/einstellungen' },
          { label: 'Airkey' },
        ]}
        actions={
          configured ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/schluesselverwaltung')}
            >
              <ExternalLink className="w-4 h-4 mr-1.5" />
              Zur Schlüsselverwaltung
            </Button>
          ) : null
        }
      />

      {/* API credentials card */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <KeySquare className="w-5 h-5 text-brand-500" />
            <Card.Title>API-Zugangsdaten</Card.Title>
          </div>
          {configured && (
            <span className="text-xs text-green-600 font-medium flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Konfiguriert
            </span>
          )}
        </Card.Header>
        <Card.Body className="space-y-4">
          <Input
            label="API-Key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={configured ? '(gespeichert – nur eingeben zum Ändern)' : 'EVVA Airkey API-Key'}
            hint="Aus dem Airkey Web-Portal unter Integration → API-Schlüssel."
          />
          <Input
            label="Base-URL"
            type="text"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://integration.api.airkey.evva.com"
            hint="In der Regel nicht ändern. Nur für On-Premise-Instanzen."
          />

          {saveMut.isError && (
            <ErrorState
              title="Speichern fehlgeschlagen"
              details={extractErrorMessage(saveMut.error)}
              size="sm"
            />
          )}

          {saveMut.isSuccess && (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              Konfiguration gespeichert.
            </div>
          )}
        </Card.Body>
        <Card.Footer className="flex items-center justify-between gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => testMut.mutate()}
            disabled={!configured || testMut.isPending}
          >
            Verbindung testen
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => saveMut.mutate()}
            disabled={(!apiKey && !configured) || saveMut.isPending}
          >
            {saveMut.isPending ? 'Wird gespeichert…' : 'Speichern'}
          </Button>
        </Card.Footer>
      </Card>

      {testResult !== null && (
        <Card>
          <Card.Body>
            {testResult.ok ? (
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Verbindung erfolgreich</p>
                  {testResult.lockingSystemCount !== undefined && (
                    <p className="text-sm text-gray-600">
                      {testResult.lockingSystemCount} Schließanlage(n) gefunden.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-red-700">
                <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Verbindung fehlgeschlagen</p>
                  {testResult.details && (
                    <details className="mt-1">
                      <summary className="text-sm cursor-pointer">Details anzeigen</summary>
                      <pre className="mt-1 text-xs whitespace-pre-wrap bg-red-50 border border-red-200 rounded p-2">
                        {testResult.details}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Onboarding sync card — only when Airkey is configured */}
      {configured && (
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-brand-500" />
              <Card.Title>HR-Onboarding-Automatisierung</Card.Title>
            </div>
          </Card.Header>
          <Card.Body className="space-y-5">
            <p className="text-sm text-gray-600">
              Gleicht täglich die TANSS-Mitarbeiterliste mit Airkey ab. Neue Mitarbeiter erhalten
              automatisch einen digitalen Schlüssel; inaktive Mitarbeiter verlieren ihre
              Berechtigung. Die Synchronisierung läuft als geplanter Job unter{' '}
              <strong>Einstellungen → Geplante Jobs</strong>.
            </p>

            <Switch
              label="Synchronisierung aktivieren"
              checked={onboardingEnabled}
              onCheckedChange={setOnboardingEnabled}
            />

            {onboardingEnabled && (
              <div className="space-y-4 pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500 pt-2">
                  Wähle das Standard-Ziel für neue Mitarbeiter. Es reicht, eines der drei Felder
                  zu setzen (Schloss, Bereich oder Schließanlage).
                </p>

                <Select
                  label="Standard-Schloss"
                  value={defaultLockId}
                  onValueChange={setDefaultLockId}
                  options={lockOptions}
                  hint="Neuen Mitarbeitern wird Zugang zu diesem Schloss gewährt."
                />

                <Select
                  label="Standard-Bereich"
                  value={defaultAreaId}
                  onValueChange={setDefaultAreaId}
                  options={areaOptions}
                  hint="Neuen Mitarbeitern wird Zugang zu diesem Bereich gewährt."
                />

                <Select
                  label="Standard-Schließanlage"
                  value={defaultLockingSystemId}
                  onValueChange={setDefaultLockingSystemId}
                  options={lockingSystemOptions}
                  hint="Neuen Mitarbeitern wird Zugang zur gesamten Schließanlage gewährt."
                />
              </div>
            )}

            {saveOnboardingMut.isError && (
              <ErrorState
                title="Speichern fehlgeschlagen"
                details={extractErrorMessage(saveOnboardingMut.error)}
                size="sm"
              />
            )}

            {saveOnboardingMut.isSuccess && (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                Onboarding-Konfiguration gespeichert.
              </div>
            )}
          </Card.Body>
          <Card.Footer className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              onClick={() => saveOnboardingMut.mutate()}
              disabled={saveOnboardingMut.isPending}
            >
              {saveOnboardingMut.isPending ? 'Wird gespeichert…' : 'Speichern'}
            </Button>
          </Card.Footer>
        </Card>
      )}
    </div>
  )
}
